import { useCallback, useEffect, useMemo } from 'react';

import { useQuery } from 'react-query';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import exercisesApi from 'api/rest/exercises/exercises';
import { Exercise } from 'api/rest/exercises/exercises.types';
import { toExerciseId } from 'api/rest/exercises/exercises.types';
import queryClient from 'api/rest/queryClient';
import sectionsApi from 'api/rest/sections/sections';
import subjectsApi from 'api/rest/subjects/subjects';
import subsectionsApi from 'api/rest/subsections/subsections';
import QUERY from 'constants/queryKeys';
import isProduction from 'services/common/isProduction';

import useExercisesSetsStore from '../exercisesSets/exercisesSets';
import exercisesToDisplayBase from './_exercisesToDisplay_base';

const storageName = 'exercises_to_display_sets';
const mediaUrl = import.meta.env.VITE_MEDIA_URL;
const SINGLE_EXERCISE_STALE_TIME_MS = 60_000;
const DETAILS_STALE_TIME_MS = 1000 * 60 * 60;

const storage = exercisesToDisplayBase(storageName);

const singleExerciseQueryKey = (exerciseId: string | undefined, currentSetId: unknown) =>
  [QUERY.GET_SINGLE_EXERCISE, exerciseId, currentSetId] as const;

const prefetchExerciseMedia = (exercise?: Exercise | null) => {
  if (mediaUrl && exercise?.files) {
    Object.values(exercise.files)
      .flat()
      .forEach(url => {
        if (!url) return;
        const image = new Image();
        image.src = `${mediaUrl}${url}`;
      });
  }
  if (exercise?.audio) {
    const audioEl = new Audio();
    audioEl.preload = 'auto';
    audioEl.src = exercise.audio;
  }
};

const prefetchExerciseDetails = (exercise: Exercise) => {
  void queryClient.prefetchQuery([QUERY.GET_SINGLE_SUBJECT, exercise.subject], subjectsApi.getSubject(exercise.subject), {
    staleTime: DETAILS_STALE_TIME_MS,
  });
  void queryClient.prefetchQuery([QUERY.GET_SINGLE_SECTION, exercise.section], sectionsApi.getSection(exercise.section), {
    staleTime: DETAILS_STALE_TIME_MS,
  });
  if (exercise.subsection) {
    void queryClient.prefetchQuery(
      [QUERY.GET_SINGLE_SUBSECTION, exercise.subsection],
      subsectionsApi.getSubsection(exercise.subsection),
      { staleTime: DETAILS_STALE_TIME_MS },
    );
  }
};

const prefetchNextExercise = (exercises: { uuid?: string }[], cursor: number, currentSetId: unknown) => {
  if (!currentSetId || !exercises?.length) return;
  const nextId = exercises[cursor + 1]?.uuid;
  if (nextId == null) return;

  const queryKey = singleExerciseQueryKey(nextId, currentSetId);
  const cached = queryClient.getQueryData<Exercise>(queryKey);
  prefetchExerciseMedia(cached);
  if (cached) prefetchExerciseDetails(cached);

  void queryClient
    .prefetchQuery(queryKey, exercisesApi.getSingle(toExerciseId(nextId)), {
      staleTime: SINGLE_EXERCISE_STALE_TIME_MS,
    })
    .then(() => {
      const exercise = queryClient.getQueryData<Exercise>(queryKey);
      prefetchExerciseMedia(exercise);
      if (exercise) prefetchExerciseDetails(exercise);
    });
};

// TODO prawodpodobnie najlepszym rozwiązaniem bedzie używanie jednego wspólnego store tutaj, a każda zmiana funkcji będzie to aktualizować
// TODO więc będize wymagana zmiana => usunięcie tej implementacji i dołączenie do exercisesToDisplayBase ew. dorzucenie wartości jaka funkcjonalnosć jest wybrana

// @ts-ignore
export const useSetsStorage = isProduction ? create(storage) : create(devtools(storage, { name: storageName }));

const useExercisesToDisplay_sets = () => {
  const exercisesToDisplayStorage = useSetsStorage();
  const { cursor, exercises, setCurrentExercise, onError } = exercisesToDisplayStorage;
  const { currentSetId } = useExercisesSetsStore();

  const exerciseId = useMemo(() => exercises[cursor]?.uuid, [cursor, exercises]);

  const { data: exerciseData } = useQuery(
    singleExerciseQueryKey(exerciseId, currentSetId),
    exerciseId != null ? exercisesApi.getSingle(toExerciseId(exerciseId)) : () => Promise.reject(new Error('Missing exercise id')),
    {
      enabled: exerciseId != null,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: SINGLE_EXERCISE_STALE_TIME_MS,
    },
  );

  const updateCurrentExercise = useCallback(async (exercise?: Exercise) => {
    if (!exercise) return;
    try {
      const [subjectDetails, sectionDetails, subsectionDetails] = await Promise.all([
        queryClient.fetchQuery([QUERY.GET_SINGLE_SUBJECT, exercise.subject], subjectsApi.getSubject(exercise.subject), {
          staleTime: DETAILS_STALE_TIME_MS,
        }),
        queryClient.fetchQuery([QUERY.GET_SINGLE_SECTION, exercise.section], sectionsApi.getSection(exercise.section), {
          staleTime: DETAILS_STALE_TIME_MS,
        }),
        exercise.subsection
          ? queryClient.fetchQuery(
              [QUERY.GET_SINGLE_SUBSECTION, exercise.subsection],
              subsectionsApi.getSubsection(exercise.subsection),
              { staleTime: DETAILS_STALE_TIME_MS },
            )
          : undefined,
      ]);
      setCurrentExercise({
        audioSource: exercise.audio || undefined,
        index: exercise.index,
        uuid: typeof exercise.id === 'string' ? exercise.id : String(exercise.id),
        globalId: exercise.globalId,
        subject: subjectDetails,
        section: sectionDetails,
        subsection: subsectionDetails,
        files: exercise.files,
        externalSources: exercise.externalSources,
        importFileIdentifier: exercise.importFileIdentifier,
        userMark: exercise.userMark,
        isDone: exercise.isDone,
      });
    } catch (e) {
      onError();
    }
  }, []);

  useEffect(() => {
    updateCurrentExercise(exerciseData);
  }, [exerciseData]);

  useEffect(() => {
    prefetchNextExercise(exercises, cursor, currentSetId);
  }, [exercises, cursor, currentSetId]);

  return exercisesToDisplayStorage;
};

export default useExercisesToDisplay_sets;
